/**
 * @fileoverview Map component for location selection and directions
 * @module ctl-map
 * @version 1.0.0
 * 
 * @description
 * A basic map component for displaying locations and providing directions.
 * Uses a placeholder/iframe approach for simplicity.
 * 
 * @example
 * <!-- Basic usage -->
 * <ctl-map address="123 Main St, City, State 12345"></ctl-map>
 * 
 * @example
 * <!-- With custom height -->
 * <ctl-map 
 *   address="123 Main St" 
 *   height="400px"
 * ></ctl-map>
 */

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    --ctl-font: system-ui, -apple-system, Segoe UI, Roboto, Arial;
    --ctl-map-height: var(--height, var(--ctl-space-75));

    display: block;
    font-family: var(--ctl-font);
    color: var(--ctl-color-text);
  }

  .container {
    display: flex;
    flex-direction: column;
    gap: var(--ctl-space-3);
    border: var(--ctl-space-0-5) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    overflow: hidden;
    background: var(--ctl-color-surface);
  }

  .map-wrapper {
    position: relative;
    width: 100%;
    height: var(--ctl-map-height);
    background: color-mix(in srgb, var(--ctl-color-muted) var(--ctl-opacity-soft), var(--ctl-color-surface));
  }

  .map-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: var(--ctl-space-3);
    padding: var(--ctl-space-6);
    text-align: center;
  }

  .map-icon {
    font-size: var(--ctl-font-size-4xl);
    opacity: var(--ctl-opacity-hover-strong);
  }

  .map-text {
    font-size: var(--ctl-font-size-base);
    color: var(--ctl-color-muted);
    max-width: var(--ctl-space-75);
  }

  .map-address {
    font-weight: var(--ctl-font-weight-semibold);
    color: var(--ctl-color-text);
  }

  .actions {
    display: flex;
    gap: var(--ctl-space-2);
    padding: var(--ctl-space-3) var(--ctl-space-4);
    border-top: var(--ctl-space-0-5) solid var(--ctl-color-border);
    background: var(--ctl-color-surface);
  }

  .action-button {
    display: inline-flex;
    align-items: center;
    gap: var(--ctl-space-1-5);
    padding: var(--ctl-space-2) var(--ctl-space-4);
    border: var(--ctl-space-0-5) solid var(--ctl-color-border);
    border-radius: var(--ctl-radius-md);
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    font-family: inherit;
    font-size: var(--ctl-font-size-base);
    font-weight: var(--ctl-font-weight-normal);
    cursor: pointer;
    transition: background var(--ctl-transition-fast) ease, border-color var(--ctl-transition-fast) ease;
    text-decoration: none;
  }

  .action-button:hover {
    background: var(--ctl-color-overlay-1);
    border-color: var(--ctl-color-text);
  }

  .action-button.primary {
    background: var(--ctl-color-brand);
    border-color: var(--ctl-color-brand);
    color: var(--ctl-color-white, white);
  }

  .action-button.primary:hover {
    background: color-mix(in srgb, var(--ctl-color-brand) var(--ctl-opacity-hover-pct), var(--ctl-color-black, black));
  }

  :host([variant="compact"]) .actions {
    padding: var(--ctl-space-2) var(--ctl-space-3);
  }

  :host([variant="compact"]) .action-button {
    padding: var(--ctl-space-1-5) var(--ctl-space-3);
    font-size: var(--ctl-font-size-md);
  }
</style>

<div class="container" part="container">
  <div class="map-wrapper" part="map-wrapper">
    <div class="map-placeholder" part="map-placeholder">
      <div class="map-icon" part="map-icon">📍</div>
      <div class="map-text" part="map-text">
        <div class="map-address" part="map-address"></div>
        <div style="margin-top: var(--ctl-space-2); font-size: var(--ctl-font-size-sm);">Map view coming soon</div>
      </div>
    </div>
  </div>
  <div class="actions" part="actions">
    <a class="action-button primary" part="directions-button" target="_blank" rel="noopener noreferrer">
      🧭 Get Directions
    </a>
    <button class="action-button" part="share-button">
      📤 Share Location
    </button>
  </div>
</div>
`;

/**
 * Map Web Component
 * 
 * @class CtlMap
 * @extends HTMLElement
 * 
 * @attr {string} address - Location address
 * @attr {string} lat - Latitude coordinate
 * @attr {string} lng - Longitude coordinate
 * @attr {string} height - Map height (default: 300px)
 * @attr {string} variant - Display variant (compact)
 * 
 * @fires ctl-directions - Dispatched when directions button is clicked
 * @fires ctl-share - Dispatched when share button is clicked
 * 
 * @csspart container - The main container
 * @csspart map-wrapper - The map wrapper
 * @csspart map-placeholder - The map placeholder
 * @csspart map-icon - The map icon
 * @csspart map-text - The map text
 * @csspart map-address - The address display
 * @csspart actions - The actions container
 * @csspart directions-button - The directions button
 * @csspart share-button - The share button
 * 
 * @cssprop --ctl-color-text - Text color
 * @cssprop --ctl-color-muted - Muted text color
 * @cssprop --ctl-color-border - Border color
 * @cssprop --ctl-color-surface - Background color
 * @cssprop --ctl-color-brand - Brand color
 * @cssprop --height - Map height
 */
export class CtlMap extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    this._addressEl = this.shadowRoot.querySelector('.map-address');
    this._directionsBtn = this.shadowRoot.querySelector('[part="directions-button"]');
    this._shareBtn = this.shadowRoot.querySelector('[part="share-button"]');
    
    // Bind handlers once
    this._boundHandleDirections = this._handleDirections.bind(this);
    this._boundHandleShare = this._handleShare.bind(this);
  }

  static get observedAttributes() {
    return ['address', 'height', 'variant'];
  }

  connectedCallback() {
    this._directionsBtn.addEventListener('click', this._boundHandleDirections);
    this._shareBtn.addEventListener('click', this._boundHandleShare);
    this._render();
  }

  disconnectedCallback() {
    this._directionsBtn.removeEventListener('click', this._boundHandleDirections);
    this._shareBtn.removeEventListener('click', this._boundHandleShare);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this._render();
    }
  }

  _handleDirections(e) {
    const address = this.getAttribute('address');
    if (!address) return;

    // Build Google Maps directions URL
    const encodedAddress = encodeURIComponent(address);
    const url = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;

    this.dispatchEvent(new CustomEvent('ctl-directions', {
      bubbles: true,
      composed: true,
      detail: { address, url }
    }));
  }

  _handleShare(e) {
    e.preventDefault();
    const address = this.getAttribute('address');
    
    // Guard against missing address
    if (!address) return;
    
    if (navigator.share) {
      navigator.share({
        title: 'Location',
        text: address,
        url: window.location.href
      }).catch(() => {
        // Fallback: copy to clipboard
        this._copyToClipboard(address);
      });
    } else {
      this._copyToClipboard(address);
    }

    this.dispatchEvent(new CustomEvent('ctl-share', {
      bubbles: true,
      composed: true,
      detail: { address }
    }));
  }

  _copyToClipboard(text) {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(() => {
        // Could show a toast notification here
      });
    }
  }

  _render() {
    // Address
    const address = this.getAttribute('address');
    if (address) {
      this._addressEl.textContent = address;
    } else {
      this._addressEl.textContent = 'No address provided';
    }

    // Height
    const height = this.getAttribute('height');
    if (height) {
      this.style.setProperty('--height', height);
    }

    // Update directions link
    if (address) {
      const encodedAddress = encodeURIComponent(address);
      this._directionsBtn.href = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
    } else {
      // Clear directions link when no address is present to avoid stale URLs
      if (this._directionsBtn) {
        this._directionsBtn.removeAttribute('href');
      }
    }
    
    // Disable share button when no address
    this._shareBtn.disabled = !address;
  }

  /**
   * Get the current address
   * @returns {string} The address
   */
  get address() {
    return this.getAttribute('address') || '';
  }

  /**
   * Set the address
   * @param {string} val - Address string
   */
  set address(val) {
    this.setAttribute('address', val);
  }
}

if (!customElements.get("ctl-map")) {
  customElements.define("ctl-map", CtlMap);
}
